import React, { useState, useEffect } from "react";
import Header from "../components/site/Header";
import Hero from "../components/site/Hero";
import ValueBar from "../components/site/ValueBar";
import UtilityBar from "../components/site/UtilityBar";
import WhyChoose from "../components/site/WhyChoose";
import OurStory from "../components/site/OurStory";
import Customization from "../components/site/Customization";
import SplitSection from "../components/site/SplitSection";
import ProductCollection from "../components/site/ProductCollection";
import Benefits from "../components/site/Benefits";
import Certifications from "../components/site/Certifications";
import PartnerLogos from "../components/site/PartnerLogos";
import Gallery from "../components/site/Gallery";
import QuoteCta from "../components/site/QuoteCta";
import Footer from "../components/site/Footer";
import QuoteDialog from "../components/site/QuoteDialog";
import CatalogDialog from "../components/site/CatalogDialog";
import AccessibilityPanel from "../components/site/AccessibilityPanel";
import MobileStickyBar from "../components/site/MobileStickyBar";
import SeoHead from "../components/site/SeoHead";

export default function HomePage() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const openQuote = () => setQuoteOpen(true);
  const openCatalog = () => setCatalogOpen(true);

  // Deep-link triggers: open quote dialog when URL contains ?quote=1 or #quote
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsQuote =
      params.get("quote") === "1" ||
      params.get("quote") === "open" ||
      window.location.hash.toLowerCase() === "#quote";
    if (wantsQuote) setQuoteOpen(true);

    const wantsCatalog =
      params.get("catalog") === "1" ||
      window.location.hash.toLowerCase() === "#catalog";
    if (wantsCatalog) setCatalogOpen(true);
  }, []);

  return (
    <div
      data-testid="homepage"
      className="bg-cream pb-16 md:pb-0 font-nunito"
    >
      <SeoHead
        title="ViewLux Colorado — Aluminum Windows & Doors · Frederick, CO"
        description="ViewLux Colorado — premium thermally broken aluminum windows and doors for the Rocky Mountains. Casement, sliding, pivot, bifold systems. Factory-direct."
        path="/"
      />
      <UtilityBar />
      <Header onRequestQuote={openQuote} onDownloadCatalog={openCatalog} />
      <Hero onRequestQuote={openQuote} onDownloadCatalog={openCatalog} />
      <ValueBar />
      <WhyChoose />
      <OurStory />

      <SplitSection
        id="precision"
        eyebrow="Engineered for Excellence"
        title={
          <>
            A system for those who{" "}
            <em className="italic font-light text-[#e2b880]">expect more.</em>
          </>
        }
        subtitle="In design, performance, and service."
        body="From advanced CNC fabrication to hand-finished frames, we combine modern machinery with skilled craftsmanship. Clean sightlines, smooth operation, and refined detailing come standard on every unit we produce."
        imageUrl="/viewlux/hero/living-forest.jpg"
        imageAlt="ViewLux interior with floor-to-ceiling aluminum window"
        reverse
        dark
        label="Living · Residence"
      />

      <SplitSection
        id="design"
        eyebrow="Modern Architecture"
        title={
          <>
            Seamless living.{" "}
            <em className="italic font-light text-[#7a5b2f]">
              Uncompromising design.
            </em>
          </>
        }
        body="Minimalist frames. Oversized glass. Uninterrupted views. ViewLux aluminum systems blend into any setting — from clean-lined contemporary builds to reimagined classic homes — enhancing light, airflow, and spatial openness."
        imageUrl="/viewlux/hero/bifold.jpg"
        imageAlt="Bi-fold door opening onto patio"
        label="VLX · 100 · Bifold"
      />

      <SplitSection
        id="custom"
        eyebrow="Made to Specification"
        title={
          <>
            Custom aluminum, for{" "}
            <em className="italic font-light text-[#e2b880]">
              any project.
            </em>
          </>
        }
        body="Your vision deserves more than a catalog standard. We custom-build each system to your exact specifications:"
        bullets={[
          "Large-span sliding doors",
          "Fixed picture windows",
          "Custom shapes and configurations",
          "Corner and angled assemblies",
        ]}
        outro="All products are available for new construction or retrofit applications — making it easier to upgrade without compromise."
        imageUrl="/viewlux/hero/corner-specialty.jpg"
        imageAlt="Corner specialty aluminum window"
        reverse
        dark
        label="Corner · Specialty"
      />

      <SplitSection
        id="durable"
        eyebrow="Built to Last"
        title={
          <>
            Durable. Energy-efficient.{" "}
            <em className="italic font-light text-[#7a5b2f]">
              Weather-resistant.
            </em>
          </>
        }
        body="ViewLux systems are engineered to perform across climates — from extreme heat and coastal humidity to cold, harsh winters."
        bullets={[
          "Thermal break technology for enhanced energy efficiency",
          "Insulated glass options for year-round indoor comfort",
          "Corrosion-resistant aluminum for moisture, salt air, and temperature shifts",
        ]}
        outro="Whether for a single-family home or a multi-unit development — designed for long-term performance with minimal maintenance."
        imageUrl="/viewlux/hero/pivot-entry.jpg"
        imageAlt="Pivot door entrance"
        label="VLX · 100 · Pivot"
      />

      <ProductCollection onRequestQuote={openQuote} />
      <Customization />
      <Benefits />
      <Certifications />
      <PartnerLogos />
      <Gallery />
      <QuoteCta onRequestQuote={openQuote} />
      <Footer onDownloadCatalog={openCatalog} />

      <QuoteDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} />
      <CatalogDialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
      />
      <AccessibilityPanel />
      <MobileStickyBar onRequestQuote={openQuote} />
    </div>
  );
}
